import type { SnapshotOrInstance } from "@gadgetinc/mobx-quick-tree";
import { action, extend, getRoot, Instance, register, types } from "@gadgetinc/mobx-quick-tree";
import { ProblemsList } from "../../problems/ProblemsList";
import { ShopifyPartnerAppConfigProblems } from "../../problems/finders/ShopifyPartnerAppConfigProblems";
import { contentHash, getAppSettings, isLiveRoot, isTreeTestRoot } from "../../utils";
import { ShopifyConnectionConfig } from "../ShopifyConnectionConfig";
import { ShopifyBasePartnerAppConfig } from "./ShopifyBasePartnerAppConfig";
import { ShopifyTomlConfig } from "./ShopifyTomlConfig";

/**
 * See packages/state-trees/src/connections/ShopifyConnectionConfig.ts for documentation
 * on the different types of Shopify configurations
 */
@register
export class ShopifyPartnerAppConfig extends extend(ShopifyBasePartnerAppConfig("ShopifyPartnerAppConfig"), {
  appURL: types.string,
  tomlConfig: types.maybe(ShopifyTomlConfig),
}) {
  get contentHash() {
    return contentHash([this.apiKey, this.apiSecretKey, this.appURL, this.appId, this.appHandle, this.appName, this.embedded]);
  }

  get appURLDomain() {
    try {
      const url = new URL(this.appURL);

      return url.hostname;
    } catch {
      return "";
    }
  }
  get appPrimaryDomain() {
    try {
      const settings = getAppSettings(this);

      const url = new URL(settings.defaultDomain);
      return url.hostname;
    } catch {
      return "";
    }
  }

  get shopifyApiVersion(): string | undefined {
    return this.shopifyConnectionConfig?.shopifyApiVersion;
  }

  get shopifyConnectionConfig(): Instance<typeof ShopifyConnectionConfig> | undefined {
    const root = getRoot(this);

    if (!root || (!isLiveRoot(root) && !isTreeTestRoot(root))) return;

    const connection = root.maybeGetShopifyConnection();
    return connection?.configuration as Instance<typeof ShopifyConnectionConfig>;
  }

  get problems(): ProblemsList {
    const problems = ShopifyPartnerAppConfigProblems(this);
    return problems;
  }

  @action
  setAppURL(value: string) {
    this.appURL = value;
  }

  @action
  setTomlConfig(config: SnapshotOrInstance<typeof ShopifyTomlConfig>) {
    this.tomlConfig = config as Instance<typeof ShopifyTomlConfig>;
  }
}
